import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Filter,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Repeat,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
  Calendar,
  DollarSign
} from "lucide-react";
import { getAccountRecurringExpenses, deleteAccountRecurringExpense } from "../core/_requests";
import { useAuth } from '../../auth';
import { ACCOUNTS_PERMS } from '../core/permissions';
import { isCustomAccountType } from '../core/expenseAccountTypes';
import { AccountsActionDropdown, DropdownPosition, getDropdownPosition } from './AccountsActionDropdown';

interface RecurringExpense {
  id: number;
  profileName: string;
  vesselId?: number | null;
  vesselName?: string | null;
  accountCode?: string | null;
  account: string;
  frequency: string;
  startDate: string;
  endDate: string;
  nextExpenseDate: string;
  vendorId: number | null;
  vendorName: string | null;
  amount: number;
  taxName?: string | null;
  taxAmount?: number | null;
  paidThrough: string;
  status: string;
}

export default function AccountsPurchaseRecurringExpenses() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState<RecurringExpense[]>([]);
  const [activeMenuId, setActiveMenuId] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const filters = ["All", "Active", "Stopped", "Expired"];

  useEffect(() => {
    setLoading(true);
    getAccountRecurringExpenses()
      .then((data) => {
        setExpenses(data || []);
      })
      .catch((err) => {
        console.error("Failed to load recurring expenses:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refreshTrigger]);

  useEffect(() => {
    const closeDropdowns = () => {
      setActiveMenuId(null);
      setDropdownPos(null);
    };
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this recurring expense profile?")) {
      return;
    }
    try {
      await deleteAccountRecurringExpense(id);
      setExpenses((prev) => prev.filter((exp) => exp.id !== id));
      handleRefresh();
    } catch (err: any) {
      console.error("Failed to delete recurring expense:", err);
      alert("Error deleting recurring expense");
    }
  };

  const safeNumber = (val: any): number => {
    let num = Number(val);
    if (isNaN(num)) {
      const clean = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : '';
      num = Number(clean);
      if (isNaN(num)) num = 0;
    }
    return num;
  };

  // Compute stats dynamically
  const activeProfiles = expenses.filter((e) => e.status === "Active");
  const monthlyCost = activeProfiles.reduce((acc, curr) => {
    const amt = safeNumber(curr.amount) + safeNumber(curr.taxAmount);
    if (curr.frequency === "Weekly") {
      return acc + amt * 4.33; // Approx weeks in a month
    } else if (curr.frequency === "Yearly") {
      return acc + amt / 12;
    }
    return acc + amt; // Default Monthly or other
  }, 0);

  const upcomingCount = activeProfiles.filter((e) => {
    if (!e.nextExpenseDate) return false;
    const nextDate = new Date(e.nextExpenseDate);
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    return nextDate >= today && nextDate <= thirtyDaysFromNow;
  }).length;

  const totalGenerated = expenses.length; // Number of profiles saved

  const stats = [
    {
      label: "Active Profiles",
      value: activeProfiles.length.toString(),
      change: "+0%",
      trend: "up",
      color: "blue",
    },
    {
      label: "Est. Monthly Cost",
      value: new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(safeNumber(monthlyCost)),
      change: "+0%",
      trend: "up",
      color: "emerald",
    },
    {
      label: "Upcoming (30 days)",
      value: upcomingCount.toString(),
      change: "0%",
      trend: "up",
      color: "amber",
    },
    {
      label: "Total Profiles",
      value: totalGenerated.toString(),
      change: "0%",
      trend: "up",
      color: "violet",
    },
  ];

  // Filtering & Search
  const filteredExpenses = expenses.filter((exp) => {
    // Filter by Status
    if (activeFilter !== "All" && exp.status !== activeFilter) {
      return false;
    }
    // Filter by Search Query
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      const matchName = exp.profileName?.toLowerCase().includes(q);
      const matchAccount = exp.account?.toLowerCase().includes(q);
      const matchVendor = exp.vendorName?.toLowerCase().includes(q);
      const matchVessel = (exp.vesselName || (exp as any).vessel_name)?.toLowerCase().includes(q);
      const matchAccountCode = (exp.accountCode || (exp as any).account_code)?.toLowerCase().includes(q);
      return matchName || matchAccount || matchVendor || matchVessel || matchAccountCode;
    }
    return true;
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-emerald-50 text-emerald-700 border-emerald-200/50";
      case "Stopped":
        return "bg-slate-100 text-slate-700 border-slate-200";
      case "Expired":
        return "bg-rose-50 text-rose-700 border-rose-200/50";
      default:
        return "bg-blue-50 text-blue-700 border-blue-200/50";
    }
  };

  const handleDownloadCSV = () => {
    if (filteredExpenses.length === 0) {
      alert("No recurring expense data to export.");
      return;
    }
    const formatCurrency = (val: any) => {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(val));
    };
    const headers = [
      "Sr. No.",
      "Profile Name",
      "Vessel",
      "Account Code",
      "Vendor",
      "Category / Account",
      "Frequency",
      "Next Expense",
      "Base Amount",
      "GST / Tax Name",
      "GST / Tax Amount",
      "Final Total",
      "Status"
    ];
    const rows = filteredExpenses.map((exp, index) => {
      const baseAmt = safeNumber(exp.amount);
      const taxAmt = safeNumber(exp.taxAmount);
      const total = baseAmt + taxAmt;
      return [
        index + 1,
        exp.profileName || '-',
        exp.vesselName || (exp as any).vessel_name || '-',
        exp.accountCode || (exp as any).account_code || '-',
        exp.vendorName || '-',
        exp.account || '-',
        exp.frequency || '-',
        exp.nextExpenseDate || '-',
        formatCurrency(baseAmt),
        exp.taxName || (taxAmt > 0 ? 'GST' : 'None'),
        formatCurrency(taxAmt),
        formatCurrency(total),
        exp.status || '-'
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `recurring_expenses_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exp = activeMenuId !== null
    ? filteredExpenses.find((exp) => exp.id === activeMenuId)
    : null;

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div
          id="sidebar-overlay"
          className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"
        ></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div
            className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5"
            id="main-scroll"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Accounts / Purchase
                </div>
                <h1 className="text-xl font-bold text-slate-900 leading-tight">
                  Recurring Expenses
                </h1>
                <p className="text-xs text-slate-500">
                  Automate repetitive expense tracking
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>Refresh data</span>
                  <button onClick={handleRefresh} className="p-1 rounded hover:bg-slate-100 transition-colors">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                  </button>
                </div>
                {hasPermission(ACCOUNTS_PERMS.RECURRING_EXPENSE_CREATE) && (
                  <button
                    onClick={() =>
                      navigate("/accounts/purchase/recurring-expenses/new")
                    }
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    New Recurring Expense
                  </button>
                )}
              </div>
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div
                      className={`w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center`}
                    >
                      <Repeat className="w-4.5 h-4.5 text-blue-600" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {stat.value}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* List and Filters */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                  {filters.map((f) => (
                    <button
                      key={f}
                      onClick={() => setActiveFilter(f)}
                      className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-all ${
                        activeFilter === f ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search profiles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 w-56 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                  </div>
                  {/* Filter button kept for future use.
                  <button className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
                    <Filter className="w-4 h-4 text-slate-500" />
                  </button>
                  */}
                  <button onClick={handleDownloadCSV} className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
                    <Download className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="p-20 flex flex-col items-center justify-center">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-4" />
                  <p className="text-sm text-slate-500 font-medium">Fetching recurring expenses...</p>
                </div>
              ) : filteredExpenses.length === 0 ? (
                <div className="p-16 flex flex-col items-center justify-center">
                  <div className="w-20 h-20 bg-linear-to-br from-blue-50 to-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mb-5">
                    <Repeat className="w-10 h-10" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    No recurring expenses found
                  </h3>
                  <p className="text-slate-500 text-sm text-center max-w-sm mb-6">
                    {searchQuery.trim() !== ""
                      ? "Try adjusting your search criteria."
                      : "Set up recurring profiles to auto-record expenses on schedule."}
                  </p>
                  {searchQuery.trim() === "" && (
                    hasPermission(ACCOUNTS_PERMS.RECURRING_EXPENSE_CREATE) && (
                      <button
                        onClick={() => navigate("/accounts/purchase/recurring-expenses/new")}
                        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Create Profile
                      </button>
                    )
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left min-w-175">
                    <thead>
                      <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50/50">
                        <th className="px-4 py-3 font-medium text-center text-slate-400 w-12">Sr. No.</th>
                        <th className="px-4 py-3 font-medium">Profile Name</th>
                        <th className="px-4 py-3 font-medium">Vessel</th>
                        <th className="px-4 py-3 font-medium">Account Code</th>
                        <th className="px-4 py-3 font-medium">Vendor</th>
                        <th className="px-4 py-3 font-medium">Category / Account</th>
                        <th className="px-4 py-3 font-medium">Frequency</th>
                        <th className="px-4 py-3 font-medium">Next Expense</th>
                        <th className="px-4 py-3 font-medium text-right">Base Amount</th>
                        <th className="px-4 py-3 font-medium text-right">GST / Tax</th>
                        <th className="px-4 py-3 font-medium text-right">Final Total</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {filteredExpenses.map((exp, index) => {
                        const baseAmt = safeNumber(exp.amount);
                        const taxAmt = safeNumber(exp.taxAmount);
                        const total = baseAmt + taxAmt;
                        return (
                        <tr
                          key={exp.id}
                          className="hover:bg-slate-50 transition-colors group cursor-pointer"
                          onClick={() => hasPermission(ACCOUNTS_PERMS.RECURRING_EXPENSE_UPDATE) && navigate(`/accounts/purchase/recurring-expenses/${exp.id}`)}
                        >
                          <td className="px-4 py-3 text-slate-500 font-medium text-center" onClick={(e) => e.stopPropagation()}>
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 font-semibold text-blue-600 hover:underline">
                            {exp.profileName}
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-700">
                            {exp.vesselName || (exp as any).vessel_name || <span className="text-slate-300">-</span>}
                          </td>
                          <td className="px-4 py-3">
                            {(exp.accountCode || (exp as any).account_code) ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60 font-mono">
                                {exp.accountCode || (exp as any).account_code}
                              </span>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-700">
                            {exp.vendorName || "-"}
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-600">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span>{exp.account || <span className="text-slate-300">N/A</span>}</span>
                              {isCustomAccountType(exp.account) && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200/80" title="Custom Account Type">
                                  Custom
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-medium">
                            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md text-[10px] font-bold">
                              {exp.frequency}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {exp.nextExpenseDate ? (
                              <div className="flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                <span>{exp.nextExpenseDate}</span>
                              </div>
                            ) : (
                              <span className="text-slate-300">None</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-mono text-right">
                            {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                              baseAmt
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-mono">
                            {taxAmt > 0 ? (
                              <div className="flex flex-col items-end">
                                <span className="text-emerald-600 font-semibold">
                                  +{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(taxAmt)}
                                </span>
                                {exp.taxName && <span className="text-[10px] text-slate-400">{exp.taxName}</span>}
                              </div>
                            ) : (
                              <span className="text-slate-300 text-xs">$0.00</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-bold text-slate-900 text-right font-mono">
                            {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                              total
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2.5 py-1 text-[10px] font-bold rounded-full border ${getStatusBadgeClass(
                                exp.status
                              )}`}
                            >
                              {exp.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right relative" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (activeMenuId === exp.id) {
                                  setActiveMenuId(null);
                                  setDropdownPos(null);
                                } else {
                                  setDropdownPos(getDropdownPosition(e.currentTarget as HTMLElement));
                                  setActiveMenuId(exp.id);
                                }
                              }}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
          </div>
        </main>
      </div>

      {exp && (
        <AccountsActionDropdown open={true} position={dropdownPos}>
                                {hasPermission(ACCOUNTS_PERMS.RECURRING_EXPENSE_UPDATE) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMenuId(null);
                                      setDropdownPos(null);
                                      navigate(`/accounts/purchase/recurring-expenses/${exp.id}`);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                  >
                                    <Edit className="w-4 h-4 text-slate-400" /> Edit Profile
                                  </button>
                                )}
                                {hasPermission(ACCOUNTS_PERMS.RECURRING_EXPENSE_DELETE) && (
                                  <>
                                    <div className="h-px bg-slate-100 my-1"></div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMenuId(null);
                                      setDropdownPos(null);
                                        handleDelete(exp.id);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                    >
                                      <Trash2 className="w-4 h-4 text-red-400" /> Delete Profile
                                    </button>
                                  </>
                                )}
        </AccountsActionDropdown>
      )}

    </>
  );
}
